import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users, ArrowsIn, ArrowsOut, UsersThree } from "@phosphor-icons/react";
import { logger } from "@/lib/logger";

export default function Aforo() {
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);

  const load = async () => {
    try {
      const [a, r] = await Promise.all([api.get("/access/aforo"), api.get("/access/list")]);
      setData(a.data);
      setRecent(r.data);
    } catch (e) { logger.warn("Aforo load failed:", e?.message); }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 2000);
    return () => clearInterval(id);
    // load estable, polling fijo → dep vacía OK
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data?.match) {
    return (
      <div className="text-center py-24 text-slate-500">
        <Users size={48} className="mx-auto mb-3" />
        Sin partido activo
      </div>
    );
  }

  const pct = data.ocupacion;
  const color = pct < 60 ? "#22C55E" : pct < 85 ? "#F97316" : "#EF4444";

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[11px] tracking-[0.25em] uppercase text-[#007AFF] font-bold">Tiempo Real</div>
        <h1 className="font-display font-bold text-4xl sm:text-5xl uppercase tracking-tighter">Aforo Live</h1>
        <div className="text-sm text-slate-500 mt-1">vs {data.match.rival} • {data.match.competicion}</div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Big circle gauge */}
        <div className="lg:col-span-2 bg-white border border-slate-200 p-6 sm:p-10 flex flex-col items-center" style={{ borderRadius: 6 }}>
          <div className="relative h-64 w-64 sm:h-80 sm:w-80">
            <svg viewBox="0 0 100 100" className="absolute inset-0 -rotate-90">
              <circle cx="50" cy="50" r="45" stroke="#E2E8F0" strokeWidth="6" fill="none" />
              <circle
                cx="50" cy="50" r="45"
                stroke={color} strokeWidth="6" fill="none" strokeLinecap="round"
                strokeDasharray={`${(Math.min(100, pct) / 100) * 283} 283`}
                style={{ transition: "stroke-dasharray 700ms ease-out, stroke 300ms" }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-[10px] tracking-[0.3em] uppercase text-slate-500 font-bold">Ocupación</div>
              <div className="font-display font-bold text-7xl sm:text-8xl tracking-tighter" style={{ color }}>{pct}%</div>
              <div className="text-slate-600 mt-1 text-sm">{data.dentro} / {data.aforo_maximo}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8 w-full">
            <Metric icon={ArrowsIn} label="Entradas" value={data.entradas} color="#22C55E" testid="metric-entradas" />
            <Metric icon={ArrowsOut} label="Salidas" value={data.salidas} color="#EF4444" testid="metric-salidas" />
            <Metric icon={Users} label="Dentro" value={data.dentro} color="#007AFF" testid="metric-dentro" />
            <Metric icon={UsersThree} label="Asistencia total" value={data.asistencia_total ?? 0} color="#0A2D5C" testid="metric-asistencia-total" />
          </div>
        </div>

        {/* By tipo */}
        <div className="space-y-3">
          <h3 className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold">Por Tipo</h3>
          {Object.entries({
            socio: "Socios", general: "General", infantil: "Infantil", invitacion: "Invitación",
            prensa: "Prensa", directiva: "Directiva", patrocinador: "Patrocinador", jugadores_cantera: "Jugadores y Cantera"
          }).map(([k, label]) => (
            <div key={k} className="bg-white border border-slate-200 px-4 py-3 flex items-center justify-between" style={{ borderRadius: 6 }}>
              <span className="text-sm text-slate-700">{label}</span>
              <span className="font-display font-bold text-2xl tracking-tighter">{data.by_tipo?.[k] ?? 0}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Recent */}
      <div>
        <h3 className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold mb-3">Últimos Movimientos</h3>
        <div className="bg-white border border-slate-200 max-h-96 overflow-y-auto" style={{ borderRadius: 6 }}>
          {recent.length === 0 ? (
            <div className="p-6 text-center text-slate-500 text-sm">Sin movimientos</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {recent.map((a) => (
                <div key={a.id} className="px-4 py-3 flex items-center justify-between text-sm">
                  <div className="flex items-center gap-3">
                    <span className={`h-2 w-2 rounded-full ${a.result === "verde" ? "bg-[#22C55E]" : a.result === "naranja" ? "bg-[#F97316]" : "bg-[#EF4444]"}`} />
                    <span className="font-medium capitalize">
                      {a.direction === "out" ? "↓ Salida" : "↑"} {a.member_name || a.tipo}
                    </span>
                    {a.numero_socio && <span className="text-xs text-slate-500">#{a.numero_socio}</span>}
                  </div>
                  <span className="text-xs text-slate-500">{new Date(a.timestamp).toLocaleTimeString("es-ES")}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, color, testid }) {
  return (
    <div className="text-center" data-testid={testid}>
      <Icon size={20} className="mx-auto" style={{ color }} />
      <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-bold mt-1">{label}</div>
      <div className="font-display font-bold text-3xl tracking-tighter" style={{ color }}>{value}</div>
    </div>
  );
}
